import { useState } from 'react';
import { Phone, CheckCircle2, Shield, Award, Star, ArrowRight, Zap, Home as HomeIcon, Building2, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PricingCard } from '../components/PricingCard';
import { RewireQuoteCalculator } from '../components/RewireQuoteCalculator';
import { SEO } from '../components/SEO';
import { companyInfo } from '../data/pricing';

export const Home = () => {
  const [showCalculator, setShowCalculator] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="House & Flat Rewiring London - From £4,950 | Crescent Electrical Ltd"
        description="Professional electrical contractor specialising in property rewiring and electrical installations across Greater London. NICEIC Approved. 1-2 bed flat rewires from £4,950."
        canonical="https://crescentelectrical.co.uk"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="text-sm uppercase tracking-wider text-blue-200 mb-4 font-semibold">
              NICEIC Approved Electrical Contractor
            </div>
            
            <h1 className="text-5xl lg:text-6xl font-extrabold mb-6 leading-tight">
              HOUSE & FLAT<br/>REWIRING
            </h1>
            
            <p className="text-xl text-blue-100 mb-6 leading-relaxed">
              Professional full & partial rewires and electrical installations across Greater London.
            </p>
            
            <div className="text-2xl font-bold text-[#F97316] mb-8">
              1–2 Bed Flat Rewires from £4,950
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button 
                size="lg"
                onClick={() => window.location.href = '/rewire-quote'}
                className="bg-[#F97316] hover:bg-[#EA580C] text-white text-lg px-8 py-6 shadow-lg"
              >
                Get A Rewire Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              
              <Button 
                size="lg"
                variant="outline"
                onClick={() => window.location.href = `tel:${companyInfo.phone}`}
                className="border-2 border-white text-white hover:bg-white hover:text-[#2563EB] text-lg px-8 py-6"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call {companyInfo.phone}
              </Button>
            </div>
            
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-blue-100">
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                NICEIC Approved
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Individual RCBO Protection
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Testing & Certification
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="bg-gray-50 py-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <Shield className="h-8 w-8 text-[#2563EB] mb-2" />
              <div className="font-semibold text-gray-900 text-sm">NICEIC</div>
              <div className="text-xs text-gray-600">Approved Contractor</div>
            </div>
            <div className="flex flex-col items-center">
              <Zap className="h-8 w-8 text-[#2563EB] mb-2" />
              <div className="font-semibold text-gray-900 text-sm">RCBO</div>
              <div className="text-xs text-gray-600">Individual Circuit Protection</div>
            </div>
            <div className="flex flex-col items-center">
              <Award className="h-8 w-8 text-[#F97316] mb-2" />
              <div className="font-semibold text-gray-900 text-sm">Certification</div>
              <div className="text-xs text-gray-600">Testing & Certification</div>
            </div>
            <div className="flex flex-col items-center">
              <HomeIcon className="h-8 w-8 text-[#F97316] mb-2" />
              <div className="font-semibold text-gray-900 text-sm">London</div>
              <div className="text-xs text-gray-600">Greater London Coverage</div>
            </div>
          </div>
        </div>
      </section>

      {/* Rewire Introduction */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Professional Property Rewiring
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Crescent Electrical Ltd specialises in complete property rewiring and electrical installations for 
            residential properties, refurbishments and developments across Greater London. Every installation includes 
            modern consumer unit with individual RCBO protection, comprehensive testing and NICEIC certification.
          </p>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Rewire Pricing
            </h2>
            <p className="text-lg text-gray-600">
              Guide prices for residential property rewiring
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <PricingCard
              tier="1–2 BED FLAT"
              price="£4,950"
              features={[
                "New electrical cabling",
                "Modern consumer unit",
                "Individual RCBO protection",
                "Testing & certification"
              ]}
              onCTAClick={() => window.location.href = '/rewire-quote'}
            />
            <PricingCard
              tier="3 BED HOUSE"
              price="£6,050 – £7,150"
              features={[
                "Complete circuit installation",
                "RCBO consumer unit",
                "Surge protection assessed",
                "NICEIC certification"
              ]}
              highlighted={true}
              onCTAClick={() => window.location.href = '/rewire-quote'}
            />
            <PricingCard
              tier="4 BED HOUSE"
              price="£7,150 – £7,700"
              features={[
                "Full property rewire",
                "Modern circuit protection",
                "Professional installation",
                "Complete certification"
              ]}
              onCTAClick={() => window.location.href = '/rewire-quote'}
            />
            <PricingCard
              tier="5 BED HOUSE"
              price="£8,250 – £9,350+"
              features={[
                "Comprehensive rewire",
                "Advanced protection",
                "Full testing",
                "Documentation provided"
              ]}
              onCTAClick={() => window.location.href = '/rewire-quote'}
            />
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 text-center mb-8">
            <p className="text-gray-900">
              <strong className="text-[#F97316]">Occupied Property Allowance:</strong> Typically £400–£800 additional depending on property conditions and requirements
            </p>
          </div>

          <p className="text-center text-gray-600 text-sm">
            These are guide prices for standard installations. Final quotation depends on property assessment and agreed specification.
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 text-center mb-12">
            What's Included in a Crescent Electrical Rewire?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              'New electrical cabling throughout',
              'Socket circuits as designed',
              'Lighting circuits as designed',
              'Appropriate dedicated circuits',
              'New consumer unit with individual RCBO protection',
              'Surge protection assessed per BS 7671',
              'Earthing and bonding assessment/upgrades where required',
              'New accessories as specified',
              'First fix cable installation',
              'Second fix accessories',
              'Comprehensive testing',
              'Electrical Installation Certificate',
              'Building Regulations notification where applicable',
              'Removal/disconnection of redundant wiring where included'
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-600 mt-8 text-sm">
            Subject to individual quotation and agreed specification
          </p>
        </div>
      </section>

      {/* Calculator */}
      {showCalculator && (
        <section className="py-16 lg:py-20 bg-gray-50" id="calculator">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Rewire Calculator
              </h2>
              <p className="text-lg text-gray-600">
                Get an estimated price range for your property
              </p>
            </div>
            <RewireQuoteCalculator />
          </div>
        </section>
      )}

      <div className="text-center py-8">
        <Button 
          onClick={() => setShowCalculator(true)}
          variant="outline"
          size="lg"
          className="border-2 border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white"
        >
          Calculate My Rewire Cost
        </Button>
      </div>

      {/* Full vs Partial */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 text-center mb-12">
            Full or Partial Rewire?
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl border-2 border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Full Rewire</h3>
              <p className="text-gray-600 mb-6">
                Appropriate where the installation requires comprehensive replacement or where major refurbishment makes full replacement sensible.
              </p>
              <ul className="space-y-3">
                {[
                  'Complete property coverage',
                  'All circuits replaced',
                  'New consumer unit',
                  'Comprehensive testing',
                  'Full certification'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl border-2 border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Partial Rewire</h3>
              <p className="text-gray-600 mb-6">
                May be appropriate where electrical work is limited to certain circuits or areas and the remaining installation is suitable to retain.
              </p>
              <ul className="space-y-3">
                {[
                  'Kitchen rewire',
                  'Extension circuits',
                  'Loft conversion',
                  'Single floor',
                  'Specific circuits'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 text-center bg-white p-8 rounded-xl border-2 border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Not Sure What You Need?</h3>
            <p className="text-gray-600 mb-6">
              Suitability depends on assessment of the existing installation. We'll advise on the most appropriate option following property inspection.
            </p>
            <Button 
              onClick={() => window.location.href = '/rewire-quote'}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white"
            >
              Get A Rewire Assessment
            </Button>
          </div>
        </div>
      </section>

      {/* Builders & Developers */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] text-white rounded-2xl p-8 lg:p-12">
            <div className="max-w-3xl">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Electrical Contractor for Refurbishments & Developments
              </h2>
              <p className="text-lg text-blue-100 mb-8">
                Crescent Electrical works with builders, property developers, landlords and refurbishment companies 
                across Greater London. Services include first fix, second fix, consumer units, new circuits, testing, 
                certification and coordination with wider works.
              </p>
              <Button 
                size="lg"
                onClick={() => window.location.href = '/contact'}
                className="bg-[#F97316] hover:bg-[#EA580C] text-white"
              >
                Discuss Your Project
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 fill-[#F59E0B] text-[#F59E0B]" />
              ))}
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Customer Reviews
            </h2>
            <p className="text-lg text-gray-600">
              Trusted by homeowners and builders across London
            </p>
          </div>
          
          <div className="text-center">
            <Button 
              variant="outline"
              onClick={() => window.location.href = '/reviews'}
            >
              Read Our Reviews
            </Button>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 text-center mb-12">
            Other Electrical Services
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Consumer Units', desc: 'Modern consumer unit installation with individual RCBO protection' },
              { title: 'EICRs', desc: 'Electrical Installation Condition Reports and compliance testing' },
              { title: 'Landlord Services', desc: 'Electrical testing and certification for rental properties' },
              { title: 'Electrical Remedials', desc: 'Remedial electrical work following inspection findings' },
              { title: 'Fault Finding', desc: 'Professional electrical fault diagnosis and repair' },
              { title: 'General Electrical Work', desc: 'Additional sockets, lighting, circuits and electrical installation' }
            ].map((service, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-[#2563EB] transition-colors">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 text-sm">{service.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={() => window.location.href = '/services'}
              variant="outline"
              size="lg"
            >
              View All Services
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 text-center mb-12">
            How It Works
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              { num: '1', title: 'Property Details', desc: 'Share your requirements' },
              { num: '2', title: 'Assessment', desc: 'Property inspection' },
              { num: '3', title: 'Written Quotation', desc: 'Detailed pricing' },
              { num: '4', title: 'First Fix', desc: 'Cable installation' },
              { num: '5', title: 'Second Fix', desc: 'Accessories fitted' },
              { num: '6', title: 'Testing & Certification', desc: 'Complete documentation' }
            ].map((step) => (
              <div key={step.num} className="bg-white p-6 rounded-xl border-2 border-gray-200 text-center">
                <div className="w-12 h-12 bg-[#2563EB] text-white rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-4">
                  {step.num}
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{step.title}</h3>
                <p className="text-xs text-gray-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Ready to Discuss Your Rewire?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Get your property rewire assessment and written quotation
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => window.location.href = '/rewire-quote'}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white"
            >
              Get A Rewire Quote
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => window.location.href = `tel:${companyInfo.phone}`}
            >
              <Phone className="mr-2 h-5 w-5" />
              {companyInfo.phone}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};